import React, { useContext, useState } from 'react';
import { InlineComment } from '@amzn/awscat-pattern-library-service-model';
import { Box, Button, Icon, Textarea, SpaceBetween } from '@amzn/awsui-components-core';

import { AppContext } from '../../contexts/AppContext';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';

interface Props {
  comment: InlineComment;
  contentId: string;
  workflowId: number;
  fetchInlineComments: () => Promise<void>;
}

const InlineCommentThread = ({ comment, contentId, workflowId, fetchInlineComments }: Props) => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

  const appContext = useContext(AppContext);
  const { authnInfo } = appContext;

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [updatedCommentData, setUpdatedCommentData] = useState<string>('');
  const [isUpdatingComment, setIsUpdatingComment] = useState<boolean>(false);
  const [isDeletingComment, setIsDeletingComment] = useState<boolean>(false);

  const { commentData, principalId, principalName, threadId, createdAt } = comment;
  const createdTime = createdAt.toFormat('LLL dd');

  const handleEditComment = () => {
    setIsEditMode(true);
    setUpdatedCommentData(comment.commentData);
  };

  // @ts-expect-error TS7031 - strict migration
  const handleCommentChange = ({ detail }) => {
    setUpdatedCommentData(detail.value);
  };

  const handleCancelEditingComment = () => {
    setUpdatedCommentData(comment.commentData);
    setIsEditMode(false);
  };

  const handleSaveComment = async () => {
    setIsUpdatingComment(true);
    const updateCommentAttributes = {
      commentId: comment.commentId,
      commentData: updatedCommentData,
    };
    await mplServiceClient.updateInlineComments(contentId, workflowId, [updateCommentAttributes]);
    setIsUpdatingComment(false);
    setIsEditMode(false);
  };

  const handleDeleteComment = async () => {
    setIsDeletingComment(true);
    const { commentId } = comment;
    await mplServiceClient.deleteInlineComment(commentId);
    setIsDeletingComment(false);
    await fetchInlineComments();
  };

  const renderCommentAction = () => {
    // @ts-expect-error TS2339 - strict migration
    const { userId } = authnInfo?.userProfile;
    if (userId !== principalId || isEditMode) {
      return null;
    }
    return (
      <div>
        <Button variant="link" onClick={handleEditComment}>
          <Icon name="edit" variant="link" />
          <Box color="text-status-info" display="inline">
            Edit
          </Box>
        </Button>
        <Button variant="link" onClick={handleDeleteComment} loading={isDeletingComment}>
          <Icon name="remove" variant="link" />
          <Box color="text-status-info" display="inline">
            Delete
          </Box>
        </Button>
      </div>
    );
  };

  const renderEditSection = () => {
    return (
      <SpaceBetween direction="vertical" size="xs">
        <Textarea onChange={handleCommentChange} value={updatedCommentData} ariaRequired />
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={handleCancelEditingComment}>Cancel</Button>
          <Button
            onClick={handleSaveComment}
            variant="primary"
            loading={isUpdatingComment}
            disabled={updatedCommentData === commentData}
          >
            Save
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    );
  };

  return (
    <div className={`inline-comment-list-${threadId ? 'reply' : 'thread'}-container`}>
      <div className={`inline-comment-list-${threadId ? 'reply' : 'thread'}-header`}>
        <Box fontSize="body-s">
          <Box fontWeight="bold" fontSize="body-s" variant="span">
            {principalName}
          </Box>
          {threadId ? ' replied' : ' commented'}
          <Box padding={{ left: 'xxs', right: 'xxs' }} variant="span" textAlign="center">
            <span className="inline-comment-list-dot" />
          </Box>
          {createdTime}
        </Box>
      </div>
      {isEditMode ? renderEditSection() : updatedCommentData ? updatedCommentData : commentData}
      {renderCommentAction()}
    </div>
  );
};

export default InlineCommentThread;
